/**
 * statsTransforms.js
 *
 * Computes the 4 summary cards (Active Bookings, Pending Approvals,
 * Items to Return, Active Projects) from real backend data.
 *
 * "Active Bookings" and "Pending Approvals" include both
 * MachineReservation records (via BookMachineAPIs.ReservedMachineGet(),
 * passed in as `reservations`) and BookWorkbench records (`workbenchBookings`).
 * If `reservations` is ever passed as an empty array (e.g. that fetch
 * fails or is removed upstream), these two cards will silently undercount —
 * worth keeping an eye on if the numbers ever look low.
 *
 * "Items to Return" uses InventoryIssue (consumables) — there is still
 * no borrow/return tracking for Machine/Equipment, so this card can
 * only ever reflect consumable inventory, not equipment like a Jigsaw
 * or Arduino kit (see prior conversation).
 */

function isWithinNextWeek(dateStr) {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekOut = new Date(today);
    weekOut.setDate(weekOut.getDate() + 7);
    return date >= today && date <= weekOut;
}

function isOverdue(dueDateStr) {
    if (!dueDateStr) return false;
    const due = new Date(dueDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
}

export function computeDashboardStats({
    reservations = [], // MachineReservation[]
    workbenchBookings = [], // BookWorkbench[]
    inventoryIssues = [], // InventoryIssue[]
    projects = [], // Project[]
}) {
    // --- Active Bookings: Approved status, not yet passed ---
    const activeReservations = reservations.filter(
        (r) => ["1", "3"].includes(r.approved_status)
    );
    const activeWorkbench = workbenchBookings.filter(
        (b) => ["1", "3"].includes(b.approved_status)
    );
    const activeBookings = activeReservations.length + activeWorkbench.length;

    const dueThisWeek =
        activeReservations.filter((r) => isWithinNextWeek(r.reserved_date)).length +
        activeWorkbench.filter((b) => isWithinNextWeek(b.booking_date)).length;

    // --- Pending Approvals ---
    const pendingApprovals =
        reservations.filter((r) => r.approved_status === "0").length +
        workbenchBookings.filter((b) => b.approved_status === "0").length;

    // --- Items to Return (consumable inventory only, see caveat) ---
    const unreturnedIssues = inventoryIssues.filter(
        (i) => i.returned_ === "No"
    );
    const itemsToReturn = unreturnedIssues.length;
    const overdueItems = unreturnedIssues.filter((i) => isOverdue(i.issued_till)).length;

    // --- Active Projects ---
    const activeProjects = projects.filter((p) => p.progress_status !== "3");
    const soloCount = activeProjects.filter((p) => !p.teammates_names?.length).length;
    const teamCount = activeProjects.length - soloCount;

    return {
        activeBookings: {
            value: activeBookings,
            subtitle: `${dueThisWeek} Due this week`,
        },
        pendingApprovals: {
            value: pendingApprovals,
            subtitle: "Waiting for admin",
        },
        itemsToReturn: {
            value: itemsToReturn,
            subtitle: `${overdueItems} overdue`,
        },
        activeProjects: {
            value: activeProjects.length,
            subtitle: `${teamCount} Team, ${soloCount} Solo`,
        },
    };
}